import { X } from 'lucide-react';
import { forwardRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { cn } from '../../lib/cn.js';
import { fieldClassName } from './input.js';

/**
 * How many chips the control accepts, and how long each may be.
 *
 * Both mirror `UpdateThreadDto` — `@ArrayMaxSize(12)` and
 * `@MaxLength(32, { each: true })`. Enforced here as well as there so the
 * thirteenth tag is refused by a field that stops accepting one, rather
 * than by a 400 that arrives after the agent has moved on to the next
 * thread. Overridable, because the limit belongs to the caller's endpoint
 * and this control knows nothing about threads.
 */
export const MAX_TAGS = 12;
export const MAX_TAG_CHARS = 32;

/**
 * Untranslated copy, handed in.
 *
 * Required rather than defaulted for the reason `RatingInput.labels` is:
 * the remove button's accessible name is all a screen reader gets, and an
 * English default would ship untranslated into a three-language product.
 */
export interface TagInputLabels {
  /** Prompt in the empty box — "Add a tag…". */
  placeholder: string;
  /** Accessible name of one chip's remove button, given the tag. */
  remove: (tag: string) => string;
}

export interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  labels: TagInputLabels;
  maxTags?: number;
  maxLength?: number;
  disabled?: boolean;
  className?: string;
  /** `FormField` generates these and hands them in. */
  id?: string;
  name?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

/**
 * Lower-cased, trimmed — the same normalisation the API applies on the way in.
 *
 * Done here so the chip somebody sees is the chip that gets stored: typing
 * "VIP" next to an existing "vip" and watching them merge on the next
 * refetch reads as the field having lost one.
 */
function normalise(tag: string, maxLength: number): string {
  return tag.trim().toLowerCase().slice(0, maxLength);
}

/**
 * Chips you type into.
 *
 * Enter and comma commit, Backspace on an empty box takes the last chip
 * back, and a paste of `vip, late, refund` becomes three — because the one
 * thing an agent does with tags is copy a handful of them from the last
 * thread that looked like this one.
 *
 * The chrome is `fieldClassName`, so it cannot drift from `Input` and
 * `Textarea`. Its `focus-visible:` rules never match on a wrapper that is
 * not itself focusable, hence the `focus-within:` pair alongside: the ring
 * belongs to the box while the caret is anywhere inside it.
 */
export const TagInput = forwardRef<HTMLInputElement, TagInputProps>(function TagInput(
  {
    value,
    onChange,
    labels,
    maxTags = MAX_TAGS,
    maxLength = MAX_TAG_CHARS,
    disabled,
    className,
    id,
    name,
    'aria-describedby': describedBy,
    'aria-invalid': invalid,
  },
  ref,
) {
  const [draft, setDraft] = useState('');
  const full = value.length >= maxTags;

  function add(candidates: string[]) {
    const next = [...value];
    for (const candidate of candidates) {
      const tag = normalise(candidate, maxLength);
      // Silently dropped rather than flagged: re-adding a tag that is
      // already on the thread is not a mistake worth an error message.
      if (!tag || next.includes(tag) || next.length >= maxTags) continue;
      next.push(tag);
    }
    setDraft('');
    if (next.length !== value.length) onChange(next);
  }

  function remove(tag: string) {
    onChange(value.filter((item) => item !== tag));
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter' || event.key === ',') {
      // Enter would otherwise submit whatever form this sits in, which on
      // the inbox panel means saving the thread with the tag still in the
      // box and therefore never saved at all.
      event.preventDefault();
      add([draft]);
      return;
    }
    if (event.key === 'Backspace' && draft === '' && value.length > 0) {
      event.preventDefault();
      remove(value[value.length - 1]!);
    }
  }

  function onPaste(event: ClipboardEvent<HTMLInputElement>) {
    const text = event.clipboardData.getData('text');
    if (!/[,\n]/.test(text)) return;
    event.preventDefault();
    add(text.split(/[,\n]/));
  }

  return (
    <div
      className={cn(
        fieldClassName,
        'flex min-h-10 flex-wrap items-center gap-1.5 px-2 py-1.5',
        'focus-within:border-accent focus-within:ring-4 focus-within:ring-accent/25',
        disabled && 'cursor-not-allowed bg-surface-sunken opacity-60',
        className,
      )}
      aria-invalid={invalid}
    >
      {value.map((tag) => (
        <span
          key={tag}
          className="flex max-w-full items-center gap-1 rounded-pill bg-surface-sunken px-2.5 py-1 text-xs font-medium text-text"
        >
          <span className="truncate">{tag}</span>
          <button
            type="button"
            disabled={disabled}
            aria-label={labels.remove(tag)}
            onClick={() => remove(tag)}
            className="shrink-0 text-text-muted transition-colors hover:text-text disabled:pointer-events-none"
          >
            <X className="size-3" />
          </button>
        </span>
      ))}

      <input
        ref={ref}
        id={id}
        name={name}
        type="text"
        value={draft}
        disabled={disabled || full}
        maxLength={maxLength}
        aria-describedby={describedBy}
        aria-invalid={invalid}
        placeholder={full ? '' : labels.placeholder}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        // Committed on the way out too. Typing a tag and clicking Save is
        // the obvious thing to do, and losing it to a missing Enter is the
        // oldest bug this control has.
        onBlur={() => add([draft])}
        className={cn(
          'min-w-24 flex-1 bg-transparent px-1 py-0.5 text-sm text-text outline-none',
          'placeholder:text-text-subtle disabled:cursor-not-allowed',
        )}
      />
    </div>
  );
});
